// ランキング・任務・お知らせ（イベント＝共同国盗り）。
import { sfx } from '../audio';
import { api, type MissionView, type ServerStatus } from '../api';
import { go, register } from '../router';
import { act, state, totalStars } from '../state';
import { esc, fmt, grantsHtml, hhmm, onAct, rewardHtml, timeAgo, until } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { avatarHtml, myAvatar } from '../ui/avatar';
import { DAILY_ALL_CLEAR, LOGIN_BONUS, META_CONFIG } from '@hyakki/core';
import { toast } from '../ui/overlay';
import { errorText, loadInto } from '../ui/status';
import '../ui/fit';

const RANK_TYPES = [
  { id: 'stage', label: '国盗り' },
  { id: 'level', label: '主Lv' },
] as const;

register('ranking', {
  title: 'ランキング',
  mount(el) {
    let type: (typeof RANK_TYPES)[number]['id'] = 'stage';
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page ranking">
      <header class="screen-head"><h1 class="screen-title">${artIcon('rank')}番付</h1></header>
      <div class="tabs seg-tabs" role="tablist">${RANK_TYPES.map((t) => `<button class="tab-btn" role="tab" data-act="type" data-t="${t.id}">${t.label}</button>`).join('')}</div><div class="body rank-body"></div></section></div>`;
    const body = el.querySelector<HTMLElement>('.body')!;
    const load = () => {
      el.querySelectorAll<HTMLElement>('[data-t]').forEach((b) => b.classList.toggle('on', b.dataset.t === type));
      void loadInto(body, 'ランキング', () => api.ranking(type), (r) => {
        const val = (x: { maxStage: number; provinces?: number; stars: number; level: number; donated: number }) =>
          type === 'stage'
            ? `<span class="rv">制圧${x.provinces ?? 0}国</span><span class="rv2"><i class="ri ri-star"></i>${x.stars}</span>`
            : `<span class="rv">主Lv.${x.level}</span><span class="rv2"></span>`;
        const me = state.me?.user.id;
        body.innerHTML = `<ol class="rank-list fit-scroll">${r.top.map((x) => `<li class="${x.userId === me ? 'me' : ''} ${x.rank <= 3 ? `top${x.rank}` : ''}"><span class="rk">${x.rank}</span><span class="rface">${avatarHtml(x.avatar ?? null, 44)}</span><b class="nm"></b>${val(x)}</li>`).join('') || `<li class="state-box">${icon('rank')}<p>まだ記録がありません</p></li>`}</ol>
          ${r.me ? `<div class="my-rank"><span class="rk">${r.me.rank != null ? r.me.rank : '—'}</span><span class="rface">${avatarHtml(myAvatar(), 44)}</span><b>${esc(state.me?.user.displayName ?? 'あなた')}</b>${val(r.me)}</div>` : `<div class="my-rank"><b>あなた</b><span class="rv"><i class="ri ri-star"></i>${totalStars()}</span></div>`}
          ${r.updatedAt ? `<p class="muted small rank-at" title="${r.cacheSec ? `${Math.round(r.cacheSec / 60)}分ごとに更新` : ''}">${icon('clock')}${hhmm(r.updatedAt)} 時点</p>` : ''}`;
        // 表示名はユーザー入力なので textContent で
        body.querySelectorAll('.rank-list .nm').forEach((n, i) => (n.textContent = r.top[i].name));
      });
    };
    load();
    return onAct(el, {
      type: (b) => {
        type = b.dataset.t as typeof type;
        load();
      },
    });
  },
});

let msTab: 'daily' | 'weekly' | 'login' = 'daily';
register('missions', {
  title: '任務',
  mount(el) {
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page missions">
      <header class="screen-head"><h1 class="screen-title">${artIcon('mission')}任務</h1><div class="end"><span class="muted small ms-reset">${icon('clock')}<span></span></span></div></header>
      <div class="tabs seg-tabs" role="tablist"><button class="tab-btn" role="tab" data-act="mt" data-t="daily">日替わり</button><button class="tab-btn" role="tab" data-act="mt" data-t="weekly">週替わり</button><button class="tab-btn" role="tab" data-act="mt" data-t="login">${icon('present')}ログイン報酬</button></div>
      <div class="body fit-scroll"></div></section></div>`;
    const body = el.querySelector<HTMLElement>('.body')!;
    let data: Awaited<ReturnType<typeof api.missions>> | null = null;
    const item = (m: MissionView) => {
      const done = m.progress >= m.target;
      return `<li class="row ms-row ${m.claimed ? 'claimed' : done ? 'done' : ''}"><span class="ms-ic">${icon(m.claimed ? 'check' : 'mission')}</span><div class="grow"><b>${esc(m.text ?? m.title ?? m.name ?? m.desc ?? m.id)}</b>
        <div class="ms-prog"><div class="meter small"><i style="width:${Math.min(100, (m.progress / Math.max(1, m.target)) * 100)}%"></i></div><small>${Math.min(m.progress, m.target)}/${m.target}</small></div></div>
        <div class="ms-rew">${rewardHtml(m.grants ?? m.reward)}</div>
        <button class="btn small ${done && !m.claimed ? 'primary' : ''}" data-id="${esc(m.id)}" ${done && !m.claimed ? '' : 'disabled'}>${m.claimed ? '受取済' : done ? '受け取る' : '未達成'}</button></li>`;
    };
    const draw = () => {
      // 受け取れるものがあるタブに赤丸
      const ready = (ms: MissionView[] = []) => ms.some((m) => !m.claimed && m.progress >= m.target);
      const dailyMs = data?.daily ?? data?.missions ?? [];
      const dot: Record<typeof msTab, boolean> = {
        daily: ready(dailyMs) || (!!data?.daily && dailyMs.length > 0 && dailyMs.every((m) => m.claimed) && !data.allClearClaimed),
        weekly: ready(data?.weekly),
        login: !!state.me?.loginBonus && !state.me.loginBonus.claimedToday,
      };
      el.querySelectorAll<HTMLElement>('[data-act="mt"]').forEach((b) => {
        const t = b.dataset.t as typeof msTab;
        b.classList.toggle('on', t === msTab);
        b.setAttribute('aria-selected', String(t === msTab));
        b.querySelector('.dot')?.remove();
        if (dot[t]) b.insertAdjacentHTML('beforeend', '<i class="dot" aria-label="受け取れる報酬あり"></i>');
      });
      // 入れ替わる時刻は時計の印と短く（詳しくは title と、あそびかたの「任務」）
      const reset = el.querySelector<HTMLElement>('.ms-reset')!;
      const when = msTab === 'weekly' ? ['月曜 0時', '毎週月曜0時に入れ替わり'] : msTab === 'daily' ? ['0時', '日本時間の0時に入れ替わり'] : null;
      reset.hidden = !when;
      reset.title = when?.[1] ?? '';
      reset.setAttribute('aria-label', when?.[1] ?? '');
      reset.querySelector('span')!.textContent = when?.[0] ?? '';
      if (msTab === 'login') return void (body.innerHTML = loginBonusStrip() || `<div class="state-box">${icon('present')}<p>ログイン報酬の情報がありません</p></div>`);
      if (!data) return;
      const r = data;
      // 応答は { daily, weekly, allClearClaimed }（古い形 { missions } にも対応）
      const daily = r.daily ?? r.missions ?? [];
      const weekly = r.weekly ?? [];
      const allDone = daily.length > 0 && daily.every((m) => m.claimed);
      body.innerHTML =
        msTab === 'daily'
          ? `<ul class="list mission-list">${daily.map(item).join('')}
            ${r.daily ? `<li class="row ms-row all-clear ${r.allClearClaimed ? 'claimed' : ''}"><span class="ms-ic">${icon('present')}</span><div class="grow"><b>日替わりをすべて達成</b></div>
              <div class="ms-rew">${rewardHtml({ soutou: META_CONFIG.sweep.ticketsAllClear, ...DAILY_ALL_CLEAR })}</div>
              <button class="btn small ${allDone && !r.allClearClaimed ? 'primary' : ''}" data-id="d:all" ${allDone && !r.allClearClaimed ? '' : 'disabled'}>${r.allClearClaimed ? '受取済' : allDone ? '受け取る' : '未達成'}</button></li>` : ''}</ul>`
          : weekly.length
            ? `<ul class="list mission-list">${weekly.map(item).join('')}</ul>`
            : `<div class="state-box">${icon('mission')}<p>今週の任務はありません</p></div>`;
    };
    const load = () =>
      loadInto(body, '任務', api.missions, (r) => {
        data = r;
        draw();
      });
    void load();
    const offAct = onAct(el, {
      mt: (b) => {
        msTab = b.dataset.t as typeof msTab;
        draw();
      },
    });
    draw();
    const click = async (e: Event) => {
      // ログイン報酬（起動時の札を閉じてしまったとき、ここから受け取る）
      const lbBtn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-lb-claim]');
      if (lbBtn) {
        lbBtn.disabled = true;
        try {
          await act(api.claimLoginBonus());
          sfx('stamp');
          setTimeout(() => sfx('reward'), 260);
          toast('ログインボーナスを受け取りました', 'good');
          draw();
        } catch (err) {
          lbBtn.disabled = false;
          toast(errorText(err), 'error');
        }
        return;
      }
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-id]');
      if (!b) return;
      b.disabled = true;
      try {
        await act(api.claimMission(b.dataset.id!));
        sfx('reward');
        toast('受け取りました', 'good');
        void load();
      } catch (err) {
        b.disabled = false;
        toast(errorText(err), 'error');
      }
    };
    el.addEventListener('click', click);
    return () => {
      offAct();
      el.removeEventListener('click', click);
    };
  },
});

register('news', {
  title: 'イベント・お知らせ',
  mount(el) {
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page news">
      <header class="screen-head"><h1 class="screen-title">${artIcon('bell')}お知らせ</h1></header>
      <div class="tabs seg-tabs" role="tablist" hidden><button class="tab-btn" role="tab" data-act="nt" data-t="event">${icon('flag')}イベント</button><button class="tab-btn" role="tab" data-act="nt" data-t="news">${icon('bell')}お知らせ</button></div>
      <div class="news-body fit-scroll"><div class="event-slot"></div><div class="body"></div></div></section></div>`;
    const slot = el.querySelector<HTMLElement>('.event-slot')!;
    const body = el.querySelector<HTMLElement>('.body')!;
    // 開催中のイベントが無ければ、イベントの欄（タブ）は出さずにお知らせだけ
    let nt: 'event' | 'news' = 'news';
    const showTab = () => {
      el.querySelectorAll<HTMLElement>('[data-act="nt"]').forEach((b) => {
        b.classList.toggle('on', b.dataset.t === nt);
        b.setAttribute('aria-selected', String(b.dataset.t === nt));
      });
      slot.hidden = nt !== 'event';
      body.hidden = nt !== 'news';
    };
    showTab();
    const offTab = onAct(el, {
      nt: (b) => {
        nt = b.dataset.t as typeof nt;
        showTab();
      },
      survey: () => go('survey'),
    });
    const drawEvent = (ev: ServerStatus['event']) => {
      const open = ev?.state === 'open';
      const pct = Math.min(100, (ev?.progress ?? 0) * 100);
      const zeni = state.resources.zeni ?? 0;
      slot.innerHTML = `<section class="event-big panel-navy ${ev?.state ?? 'none'}"><span class="ev-tag tag ${open ? 'red' : ''}">${open ? '開催中' : ev?.state === 'soon' ? '予告' : '開催なし'}</span>
        <h2>${icon('flag')}${esc(ev?.title ?? '共同国盗り')}</h2>
        ${
          open
            ? `<p>全員の奉納で日本の国が塗られていきます。週替わり（毎週月曜0時に切り替え）・終了まで ${until(ev!.until)}</p>
               ${ev!.goal ? `<div class="meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}"><i style="width:${pct}%"></i></div>
               <p class="ev-nums"><b>${fmt(ev!.total ?? 0)}</b> / ${fmt(ev!.goal)}銭（${Math.floor(pct)}%）${ev!.provinces ? `・制覇 <b>${ev!.provinces.taken}</b> / ${ev!.provinces.total}国` : ''}${ev!.donors != null ? `・参加 ${fmt(ev!.donors)}人` : ''}</p>` : ''}
               ${ev!.rules?.length ? `<h3>今週の決まりごと</h3><ul class="ev-rules">${ev!.rules.map((r) => `<li>${esc(r)}</li>`).join('')}</ul>` : ''}
               <form class="donate-form"><label>奉納する銭<input type="number" name="amount" min="1000" step="1000" value="${Math.min(10000, Math.max(1000, Math.floor(zeni / 1000) * 1000))}" inputmode="numeric"></label>
                 <button class="btn primary" ${zeni >= 1000 ? '' : 'disabled'}>奉納する</button><small class="muted">所持 ${fmt(zeni)}銭・奉納額は番付「奉納」に載ります</small></form>`
            : ev?.state === 'soon'
              ? `<p>開始まで ${until(ev.until)}</p>`
              : '<p class="muted">現在開催中のイベントはありません。</p>'
        }</section>`;
    };
    const load = () =>
      loadInto(body, 'お知らせ', api.status, (s) => {
        const tabs = el.querySelector<HTMLElement>('.news .seg-tabs')!;
        if (s.event && tabs.hidden) nt = 'event';
        tabs.hidden = !s.event;
        if (!s.event) nt = 'news';
        showTab();
        drawEvent(s.event);
        const list = s.announcements ?? [];
        // 受付中のアンケートは先頭の札（答えるまで印・お礼を出す）
        const sv = state.me?.survey;
        const svRow = sv
          ? `<li class="row news-row sv-row"><div class="grow"><div class="nr-head"><b>${esc(sv.title)}</b>${sv.answered ? '<span class="tag ok">回答済み</span>' : '<span class="tag red">受付中</span>'}</div>${sv.answered ? '' : grantsHtml(sv.reward)}</div><button class="btn small ${sv.answered ? '' : 'primary'}" data-act="survey">${sv.answered ? '答え直す' : '答える'}</button></li>`
          : '';
        body.innerHTML =
          list.length || svRow
            ? `<ul class="list news-list">${svRow}${list.map((n) => `<li class="row news-row"><div class="grow"><div class="nr-head"><b>${esc(n.title)}</b><small class="muted">${timeAgo(n.startsAt)}</small></div><p class="small pre">${esc(n.body)}</p></div></li>`).join('')}</ul>`
            : `<div class="state-box empty">${icon('bell')}<p>お知らせはありません</p></div>`;
        try {
          localStorage.setItem('hk.newsSeen', String(Date.now()));
        } catch {
          /* 無視 */
        }
      });
    void load();
    const submit = async (e: Event) => {
      const f = (e.target as HTMLElement).closest<HTMLFormElement>('.donate-form');
      if (!f) return;
      e.preventDefault();
      const amount = Math.floor(Number(new FormData(f).get('amount')));
      if (!(amount >= 1000)) return toast('奉納は1,000銭からです', 'error');
      if (amount > (state.resources.zeni ?? 0)) return toast('銭が足りません', 'error');
      const btn = f.querySelector<HTMLButtonElement>('button')!;
      btn.disabled = true;
      try {
        await act(api.hounou(amount));
        toast(`${fmt(amount)}銭を奉納しました`, 'good');
        void load();
      } catch (x) {
        toast(errorText(x), 'error');
        btn.disabled = false;
      }
    };
    el.addEventListener('submit', submit);
    return () => {
      offTab();
      el.removeEventListener('submit', submit);
    };
  },
});

/** ログイン報酬（7日の周期・累計日数）。受け取りは起動時の演出で行う */
function loginBonusStrip(): string {
  const lb = state.me?.loginBonus;
  if (!lb) return '';
  const cycle: unknown[] = Array.isArray(lb.cycle) ? lb.cycle : LOGIN_BONUS.cycle;
  const n = cycle.length || 7;
  const today = (((lb.claimedToday ? lb.days - 1 : lb.days) % n) + n) % n;
  // 次の節目（30日ごとの特典）。今日の日数（未受け取りなら days+1）から数え、今日が節目で受け取り済みなら次の節目
  const every = LOGIN_BONUS.milestoneEvery;
  const cur = lb.days + (lb.claimedToday ? 0 : 1);
  const milestone = lb.claimedToday && cur % every === 0 ? cur + every : Math.ceil(cur / every) * every;
  return `<section class="lb-strip"><h2 class="sec-title">${icon('present')}ログイン報酬<span class="chip lb-ms">${milestone}日目${rewardHtml(LOGIN_BONUS.milestone)}</span><small>累計 ${lb.days}日</small></h2><ol class="lb-days">${Array.from({ length: n }, (_, i) => {
    const state2 = i < today || (i === today && lb.claimedToday) ? 'got' : i === today ? 'today' : '';
    return `<li class="${state2}"><span class="lb-day">${i + 1}日目</span><div class="lb-reward">${cycle[i] ? rewardHtml(cycle[i]) : ''}</div>${state2 === 'got' ? `<i class="lb-stamp">${icon('check')}</i>` : ''}</li>`;
  }).join('')}</ol>${lb.claimedToday ? '' : '<button class="btn primary wide lb-claim" data-lb-claim>受け取る</button>'}</section>`;
}
